"use client";
import React, { useState } from "react";
import "@/styles/components/faq_section.scss";

import Navbar from "@/components/common/Navbar";
import Footer from "@/components/common/Footer";
import Image from "next/image";

const faqs = [
  {
    question: "What is Baskitt?",
    answer:
      "Baskitt is a mobile shopping app that allows you to browse, order, and receive products from international stores in the USA, UK, and UAE — with more countries coming soon. You can shop directly through the Baskitt app and choose to have your items delivered to your address or collected from a convenient location near you.",
  },
  {
    question: "How do I download the Baskitt app?",
    answer:
      "You can download Baskitt from the Apple App Store or Google Play Store. Simply search for 'Baskitt', download, and install it on your smartphone.",
  },
  {
    question: "Do I need an account to use Baskitt?",
    answer:
      "Yes. Creating a free account allows you to save your personal details, track your orders, receive real-time updates, and enjoy a personalized shopping experience.",
  },
  {
    question: "How does Baskitt find the best pick-up location for me?",
    answer:
      "With your permission, the app uses your location to show nearby collection points where your Baskitt orders can be delivered for pickup.",
  },
  {
    question: "Are the products shown in real time?",
    answer:
      "Yes. Baskitt displays live stock availability from marketplaces, so you only see items that are currently available.",
  },
  {
    question: "Can I shop from multiple stores in one order?",
    answer:
      "Yes. You can add items from different stores within the same international marketplace into one basket and complete a single checkout. However, separate checkouts are required for each international marketplace (for example, USA and UK orders must be completed separately).",
  },
  {
    question: "How do I know if a product is available for delivery?",
    answer:
      "Each product listing clearly shows availability and estimated delivery time. If an item is out of stock or unavailable for delivery, it cannot be added to your basket.",
  },
  {
    question: "How fast is delivery?",
    answer:
      "Delivery times vary depending on the store, the marketplace country, and carrier schedules. Estimated delivery times are displayed during checkout.",
  },
  {
    question: "Can I choose Click & Collect instead of delivery?",
    answer:
      "Yes. At checkout, you can choose either home delivery or Click & Collect at a convenient location. You’ll receive a notification when your order is ready for delivery or pickup.",
  },
  {
    question: "How much does delivery cost?",
    answer:
      "Delivery fees depend on the store and your location. The exact delivery cost is shown before you confirm your order.",
  },
  {
    question: "Can I track my order?",
    answer:
      "Yes. You can track your order in real time from the 'Orders' section within the app.",
  },
  {
    question: "What payment methods does Baskitt accept?",
    answer:
      "Baskitt accepts debit and credit cards as well as other digital payment methods supported within the app.",
  },
  {
    question: "Are prices the same as in-store?",
    answer:
      "Yes. Product prices reflect the store’s pricing. Additional charges may apply, including store delivery fees to the Baskitt facility, international shipping costs, and customs duties or local taxes where applicable. All charges are clearly itemized and shown before you confirm your order.",
  },
  {
    question: "Will I receive a receipt?",
    answer:
      "Yes. A digital receipt is available in your order history, and you will also receive a confirmation email.",
  },
  {
    question: "Can I change or cancel my order after placing it?",
    answer:
      "This depends on the store and order status. If the store has not started preparing your order, you may be able to cancel or modify it in the app. Once international shipping preparation begins, cancellation is no longer possible.",
  },
  {
    question: "What if an item becomes unavailable after I place my order?",
    answer:
      "If a store cannot fulfill an item, you will be notified. You may be offered a replacement item or a refund for the unavailable product.",
  },
  {
    question: "What should I do if my order is late or missing?",
    answer:
      "Contact support through the app’s Help section. Our team will coordinate with the store or delivery partner and update you promptly.",
  },
  {
    question: "What if I receive a wrong or damaged item?",
    answer:
      "Report the issue through in-app support. Baskitt will work with the store to arrange a replacement or refund as appropriate.",
  },
  {
    question: "Is my payment information secure?",
    answer:
      "Yes. Baskitt uses secure, encrypted payment processing and does not store your full card details.",
  },
  {
    question: "How do I update my personal details or address?",
    answer:
      "Go to Account → Profile. From there you can update your name, email, phone number, and delivery addresses.",
  },
  {
    question: "I forgot my password — what should I do?",
    answer:
      "Use the 'Forgot Password' option on the login screen and follow the reset instructions.",
  },
  {
    question: "How do returns work?",
    answer:
      "Returns are subject to each store’s return policy. You can initiate a return request through the app, and Baskitt will guide you through the process.",
  },
  {
    question: "How long do refunds take?",
    answer:
      "Refund timelines depend on the payment provider. Most refunds are processed within a few business days after the store approves the return.",
  },
  {
    question: "How do I contact customer support?",
    answer:
      "Download the app, go to the Help Desk section to submit a support request, or tap 'Contact Us' to chat with our team instantly.",
  },
  {
    question: "What are your support hours?",
    answer:
      "Support hours may vary. However, our team responds promptly during standard business hours from 09:00 AM to 6:00 PM local time.",
  },
];

export default function FAQPage() {
  const [active, setActive] = useState(null);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState("");

  const toggleFAQ = (index) => {
    setActive(active === index ? null : index);
  };

  const sendFAQ = async () => {
    const name = document.querySelector('input[type="text"]').value;
    const subject = document.querySelector("select").value;
    const message = document.querySelector("textarea").value;

    try {
      setSending(true);
      setStatus("");

      const res = await fetch("/api/faq-question", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, subject, message }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to send");
      }

      setStatus("Message sent successfully.");
    } catch (error) {
      console.error(error);
      setStatus("Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="section">
        <Navbar />
      </div>

      {/* FAQ HEADER */}
      <section id="faq_header">
        <h1>FAQs</h1>

        <div className="box_image">
          <Image src="/images/faq.png" width={120} height={120} alt="box" />
        </div>
      </section>

      {/* FAQ SECTION */}
      <section id="faq_section">
        <div className="faq_wrapper">
          {/* LEFT SIDE FAQ */}
          <div className="faq_list">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq_item ${active === index ? "active" : ""}`}
              >
                <div className="faq_question" onClick={() => toggleFAQ(index)}>
                  <h4>{faq.question}</h4>
                  <span className="icon">⌄</span>
                </div>

                <div className="faq_answer">{faq.answer}</div>
              </div>
            ))}
          </div>

          {/* RIGHT SIDE QUESTION BOX */}
          <div className="faq_form">
            <h3>Ask Your Question</h3>
            <p>Ask Anything, We're Here to Help</p>

            <div className="form_group">
              <label>Name</label>
              <input type="text" />
            </div>

            <div className="form_group">
              <label>How can we help you?</label>
              <select>
                <option>Exchanges & Returns</option>
                <option>Orders</option>
                <option>Payment</option>
              </select>
            </div>

            <div className="form_group">
              <label>Message</label>
              <textarea placeholder="Your message"></textarea>
            </div>

            <button className="send_btn" onClick={sendFAQ} disabled={sending}>
              {sending ? "Sending..." : "Send Request"}
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
